import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import Modal from '../../components/ui/Modal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit2, Trash2, Trophy, Target, Gift, Calendar, Zap, Users, DollarSign, Gamepad2, Star, ToggleLeft, ToggleRight, BarChart3 } from 'lucide-react';

const TASK_TYPES = [
  { value: 'daily_login', label: 'Daily Login', icon: Calendar, desc: 'Reward for logging in each day' },
  { value: 'weekly_login', label: 'Weekly Login Streak', icon: Calendar, desc: 'Login X days in a week' },
  { value: 'total_deposit', label: 'Deposit Amount', icon: DollarSign, desc: 'Deposit a total dollar amount' },
  { value: 'deposit_count', label: 'Deposit Count', icon: DollarSign, desc: 'Make X deposits' },
  { value: 'total_spend', label: 'Spend Cash (Loads)', icon: Zap, desc: 'Spend wallet dollars loading into games' },
  { value: 'weekly_spend', label: 'Weekly Spend', icon: Zap, desc: 'Spend X dollars in a week' },
  { value: 'total_win', label: 'Win Cash', icon: Trophy, desc: 'Win a total dollar amount' },
  { value: 'play_games', label: 'Play Games', icon: Gamepad2, desc: 'Play X different games' },
  { value: 'refer_friends', label: 'Refer Friends', icon: Users, desc: 'Refer X friends' },
  { value: 'first_deposit', label: 'First Deposit', icon: Star, desc: 'Make your first deposit' },
  { value: 'custom', label: 'Custom Task', icon: Target, desc: 'Custom task with manual tracking' },
];

const PERIODS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'none', label: 'Lifetime / One-time' },
  { value: 'once', label: 'Once Ever' },
];

const ICONS = ['calendar', 'trophy', 'target', 'gift', 'zap', 'star', 'dollar', 'gamepad', 'users', 'flame', 'crown', 'medal'];

export default function TasksRewardsPage() {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const defaultForm = {
    title: '', description: '', task_type: 'daily_login', target_value: 1,
    reward_sc: 5, period: 'daily', is_recurring: true, max_claims: 1,
    enabled: true, icon: 'calendar', sort_order: 0,
  };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [tasksRes, statsRes] = await Promise.all([
        api.get('/api/admin/tasks'),
        api.get('/api/admin/tasks/stats'),
      ]);
      setTasks(tasksRes.data.tasks || []);
      setStats(statsRes.data || {});
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/api/admin/tasks/${editing.id}`, form);
        toast.success('Task updated');
      } else {
        await api.post('/api/admin/tasks', form);
        toast.success('Task created!');
      }
      setShowForm(false);
      setEditing(null);
      setForm(defaultForm);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save task');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task and all related progress?')) return;
    try {
      await api.delete(`/api/admin/tasks/${id}`);
      toast.success('Task deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (task) => {
    try {
      await api.put(`/api/admin/tasks/${task.id}`, { enabled: !task.enabled });
      toast.success(task.enabled ? 'Task disabled' : 'Task enabled');
      fetchAll();
    } catch { toast.error('Failed to toggle'); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title, description: t.description || '', task_type: t.task_type,
      target_value: t.target_value, reward_sc: t.reward_sc, period: t.period || 'none',
      is_recurring: t.is_recurring, max_claims: t.max_claims || 1,
      enabled: t.enabled, icon: t.icon || 'calendar', sort_order: t.sort_order || 0,
    });
    setShowForm(true);
  };

  const getTypeIcon = (type) => {
    const found = TASK_TYPES.find(t => t.value === type);
    return found ? found.icon : Target;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="tasks-rewards-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#D4AF37]" /> Tasks & Rewards
          </h2>
          <p className="text-sm text-gray-400">Create missions for players to earn FP rewards</p>
        </div>
        <Button onClick={() => { setForm(defaultForm); setEditing(null); setShowForm(true); }} className="bg-[#D4AF37] text-black" data-testid="add-task-btn">
          <Plus className="w-4 h-4 mr-2" /> New Task
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Active Tasks', val: stats.active_tasks || 0, color: 'green', icon: Target },
          { label: 'Total Tasks', val: stats.total_tasks || 0, color: 'blue', icon: BarChart3 },
          { label: 'Total Claims', val: stats.total_claims || 0, color: 'purple', icon: Gift },
          { label: 'FP Rewarded', val: `${(stats.total_sc_rewarded || 0).toLocaleString()} FP`, color: '[#D4AF37]', icon: Trophy },
        ].map((s, i) => (
          <div key={i} className="glass-card rounded-xl p-4" data-testid={`task-stat-${i}`}>
            <div className="flex items-center gap-2 mb-1">
              <s.icon className={`w-4 h-4 text-${s.color}-400`} />
              <span className="text-xs text-gray-400">{s.label}</span>
            </div>
            <p className="text-lg font-bold">{s.val}</p>
          </div>
        ))}
      </div>

      {/* Task List */}
      <div className="space-y-2">
        {tasks.map(task => {
          const TypeIcon = getTypeIcon(task.task_type);
          return (
            <div key={task.id} className={`glass-card rounded-xl p-4 ${!task.enabled ? 'opacity-50' : ''}`} data-testid={`task-${task.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                    <TypeIcon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-sm truncate">{task.title}</h3>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${task.enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {task.enabled ? 'ACTIVE' : 'DISABLED'}
                      </span>
                      {task.is_recurring && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400">RECURRING</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{task.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 ml-4">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-bold text-[#D4AF37]">{task.reward_sc} FP</p>
                    <p className="text-[10px] text-gray-500">Target: {task.target_value} | {task.period}</p>
                  </div>
                  <div className="text-right hidden md:block">
                    <p className="text-xs text-gray-400">{task.claims_count || 0} claims</p>
                    <p className="text-[10px] text-gray-500">{task.active_players || 0} players</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleToggle(task)} className="p-1.5 rounded-lg hover:bg-white/5" data-testid={`toggle-task-${task.id}`}>
                      {task.enabled ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5 text-gray-500" />}
                    </button>
                    <button onClick={() => openEdit(task)} className="p-1.5 rounded-lg hover:bg-white/5"><Edit2 className="w-4 h-4 text-gray-400" /></button>
                    <button onClick={() => handleDelete(task.id)} className="p-1.5 rounded-lg hover:bg-white/5"><Trash2 className="w-4 h-4 text-red-400" /></button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {tasks.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No tasks created yet. Click "New Task" to get started.</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Task' : 'Create New Task'} maxWidth="max-w-lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Task Type Selector */}
          {!editing && (
            <div>
              <label className="text-xs text-gray-400 mb-2 block">Task Type</label>
              <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto bg-[#0B0E14] rounded-lg p-2">
                {TASK_TYPES.map(tt => (
                  <button key={tt.value} type="button" onClick={() => {
                    const autoP = tt.value.includes('daily') ? 'daily' : tt.value.includes('weekly') ? 'weekly' : tt.value === 'first_deposit' ? 'once' : 'none';
                    setForm(p => ({ ...p, task_type: tt.value, period: autoP, is_recurring: autoP === 'daily' || autoP === 'weekly' }));
                  }}
                    className={`flex items-center gap-2 p-2 rounded-lg text-left text-xs transition-colors ${form.task_type === tt.value ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/50' : 'hover:bg-white/5 text-gray-400'}`}
                    data-testid={`tasktype-${tt.value}`}>
                    <tt.icon className="w-4 h-4 flex-shrink-0" />
                    <div>
                      <p className="font-medium">{tt.label}</p>
                      <p className="text-[10px] opacity-60">{tt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            placeholder="Task Title (e.g. Daily Login Bonus)" className="bg-[#0B0E14] border-white/10" required data-testid="task-title" />

          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Description shown to players..." rows={2}
            className="w-full rounded-md bg-[#0B0E14] border border-white/10 text-white p-3 text-sm" />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Target Value</label>
              <Input type="number" min={1} step="any" value={form.target_value}
                onChange={e => setForm(p => ({ ...p, target_value: parseFloat(e.target.value) || 1 }))}
                className="bg-[#0B0E14] border-white/10" data-testid="task-target" />
              <p className="text-[10px] text-gray-500 mt-0.5">e.g. 1 login, $100, 5 games</p>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Reward (FP)</label>
              <Input type="number" min={0} step="any" value={form.reward_sc}
                onChange={e => setForm(p => ({ ...p, reward_sc: parseFloat(e.target.value) || 0 }))}
                className="bg-[#0B0E14] border-white/10" data-testid="task-reward" />
              <p className="text-[10px] text-gray-500 mt-0.5">FP credited on claim</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Period</label>
              <select value={form.period} onChange={e => setForm(p => ({ ...p, period: e.target.value }))}
                className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white" data-testid="task-period">
                {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Max Claims (per period)</label>
              <Input type="number" min={1} value={form.max_claims}
                onChange={e => setForm(p => ({ ...p, max_claims: parseInt(e.target.value) || 1 }))}
                className="bg-[#0B0E14] border-white/10" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={form.is_recurring}
                onChange={e => setForm(p => ({ ...p, is_recurring: e.target.checked }))}
                className="w-4 h-4 rounded accent-[#D4AF37]" />
              <span className="text-gray-300">Recurring (resets each period)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={form.enabled}
                onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))}
                className="w-4 h-4 rounded accent-[#D4AF37]" />
              <span className="text-gray-300">Enabled</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-white/5">
            <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-white/10">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-[#D4AF37] text-black" data-testid="task-submit">
              {submitting ? 'Saving...' : editing ? 'Update Task' : 'Create Task'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
